import { useTranslation } from "react-i18next";

import { useReferral } from "@/entities/profile";
import { useShareReferral } from "@/features/share-referral";
import { formatMoney } from "@/shared/lib";
import { Button, CopyButton } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Реферальная программа. Figma: `Profile · Referral` (27:62).
 *
 * Ссылка приходит с бэкенда готовой — клиент её не собирает: имя бота знает
 * только сервер. Бонус начисляется за первое подтверждённое пополнение
 * приглашённого, поэтому счётчик приглашений и начисленная сумма расходятся,
 * и показываются обе цифры.
 */
export function ReferralPage() {
  const { t } = useTranslation();
  const share = useShareReferral();
  const { data, isPending, isError, refetch } = useReferral();

  if (isPending) return <ScreenSkeleton blocks={[120, 96, 50]} />;

  if (isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{ label: t("states.error.action"), onClick: () => void refetch() }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("profile.menu.referral")} subtitle={t("profile.referral.subtitle")} />

      <section className="flex flex-col gap-2 rounded-3xl border border-border-subtle bg-background-elevated p-5">
        <span className="text-caption font-medium text-text-secondary">
          {t("profile.referral.bonus", {
            amount: formatMoney(data.bonus_per_invite, { suffix: "USDT" }),
          })}
        </span>
        <span className="text-display font-extrabold text-text-primary">{data.code}</span>
      </section>

      <section className="flex items-center gap-3 rounded-2xl border border-border-subtle bg-background-elevated p-4">
        <span className="min-w-0 flex-1 text-caption break-all text-text-secondary">{data.link}</span>
        <CopyButton value={data.link} />
      </section>

      <dl className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1 rounded-2xl border border-border-subtle bg-background-elevated p-4">
          <dt className="text-caption font-medium text-text-secondary">
            {t("profile.referral.invited")}
          </dt>
          <dd className="text-h3 font-semibold text-text-primary">{data.invited_count}</dd>
        </div>
        <div className="flex flex-col gap-1 rounded-2xl border border-border-subtle bg-background-elevated p-4">
          <dt className="text-caption font-medium text-text-secondary">
            {t("profile.referral.earned")}
          </dt>
          <dd className="text-h3 font-semibold text-text-primary">
            {formatMoney(data.earned_total, { suffix: "USDT" })}
          </dd>
        </div>
      </dl>

      <Button size="cta" block onClick={() => share(data.link)}>
        {t("profile.referral.share")}
      </Button>
    </div>
  );
}
